// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — fiche d'une organisation : identité et état, puis sections (Instances et mandats,
// Dispositifs, À traiter, Historique, Pièces). Lecture : clé transverse ou mandat dans l'organisation ;
// un refus du serveur (403) s'affiche proprement, sans aucune donnée.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, MoreVertical, Power } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Bouton, BandeauInfo, MenuActions, Squelette } from '../../design/composants';
import { useDs, toArray, libelle, TYPES_ORGANISATION, STATUTS_JURIDIQUES, ETATS_ORGANISATION, NATURES_MANDAT } from './api';
import { useEspaces, droitsDans, invaliderEspaces } from './espaces';
import { useLecture, ErreurServeur, AccesRefuseDs, Etat, NavOrganisation, FenetreConfirmer } from './commun';
import { FenetreEtatOrganisation, passagesOrganisation } from './FenetresStructure';
import InstancesSection from './InstancesSection';
import { DispositifsSection, ATraiterSection, HistoriqueSection, PiecesSection } from './SectionsOrganisation';
import PretsSection from './PretsSection';
import AidesSection from './AidesSection';
import TresorerieSection from './TresorerieSection';
import ComptabiliteSection from './ComptabiliteSection';
import RetenuesSection from './RetenuesSection';
import CampagnesSection from './CampagnesSection';
import RapportsSection from './rapports/RapportsSection';
import { CarteDeports } from './Deports';

const TITRES = {
  instances: 'Instances et mandats', dispositifs: 'Dispositifs', 'a-traiter': 'À traiter', historique: 'Historique', pieces: 'Pièces',
  prets: 'Prêts', aides: 'Aides', campagnes: 'Campagnes', tresorerie: 'Trésorerie', comptabilite: 'Comptabilité', retenues: 'Retenues', rapports: 'Rapports',
};
/** Onglets métier visibles selon les clés de mes mandats (le serveur reste juge de chaque lecture). */
const CLES_MODULES = {
  prets: ['ds_pret_lire', 'ds_pret_saisir_guichet'],
  aides: ['ds_aide_lire', 'ds_aide_saisir_guichet', 'ds_controle_lecture'],
  campagnes: ['ds_campagne_lire', 'ds_campagne_preparer', 'ds_campagne_valider', 'ds_controle_lecture', 'ds_inscription_saisir_guichet', 'ds_remise_effectuer',
    'ds_achat_preparer', 'ds_reception_signer'],
  tresorerie: ['ds_finance_lire', 'ds_controle_lecture', 'ds_paiement_preparer', 'ds_paiement_signer', 'ds_paiement_second_signer', 'ds_paiement_executer'],
  comptabilite: ['ds_finance_lire', 'ds_controle_lecture', 'ds_ecriture_saisir', 'ds_ecriture_valider', 'ds_periode_cloturer_demander', 'ds_periode_cloturer_confirmer'],
  retenues: ['ds_retenues_lire', 'ds_paie_consulter', 'ds_fondement_enregistrer', 'ds_fondement_confirmer', 'ds_retenues_arreter_demander', 'ds_retenues_arreter_confirmer'],
  rapports: ['ds_rapport_lire', 'ds_controle_lecture', 'ds_rapport_arreter', 'ds_export_nominatif'],
};
const modulesVisibles = (droits) => Object.keys(CLES_MODULES).filter((m) => CLES_MODULES[m].some((c) => droits.a(c)));

function Identite({ o, mandats }) {
  const tenues = toArray(mandats).map((m) => `${m.fonction_libelle}${m.nature !== 'TITULAIRE' ? ` (${libelle(NATURES_MANDAT, m.nature).toLowerCase()})` : ''}`);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '-8px 0 16px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
      <Etat table={ETATS_ORGANISATION} code={o.etat} />
      <span>{libelle(TYPES_ORGANISATION, o.type)}</span>
      <span aria-hidden="true">·</span><span>{o.code}</span>
      {o.statut_juridique && o.statut_juridique !== 'A_QUALIFIER' && <><span aria-hidden="true">·</span><span>{libelle(STATUTS_JURIDIQUES, o.statut_juridique)}</span></>}
      {o.donnees_demo && <><span aria-hidden="true">·</span><span>Données de démonstration</span></>}
      {tenues.length > 0 && <><span aria-hidden="true">·</span><span>Vous y êtes : {tenues.join(', ')}</span></>}
    </div>
  );
}

export default function OrganisationPage({ section }) {
  const { org } = useParams();
  const ds = useDs();
  const { espaces, recharger: rechargerEspaces } = useEspaces();
  const detail = useLecture(() => ds.organisation(org), [ds, org]);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const o = detail.donnees;
  const droits = droitsDans(espaces, org);
  const administreStructure = droits.a('ds_organisations_administrer');
  const administreMandats = droits.a('ds_mandats_administrer');

  const fil = [{ libelle: 'Dispositifs sociaux', vers: '/dispositifs' }, { libelle: o ? o.libelle : 'Organisation' }];
  const apres = (message) => { setFenetre(null); setInfo(message); detail.recharger(); invaliderEspaces(); rechargerEspaces(); };

  const actions = o && administreStructure ? (
    <>
      {o.etat === 'BROUILLON' && <Bouton variante="primaire" Icone={CheckCircle2} onClick={() => setFenetre('valider')}>Mettre en service</Bouton>}
      {passagesOrganisation(o.etat).length > 0 && (
        <MenuActions iconeSeule Icone={MoreVertical} libelle="Actions sur l'organisation"
          items={[{ libelle: 'Changer l\'état (sommeil, dissolution)', Icone: Power, onClick: () => setFenetre('etat') }]} />
      )}
    </>
  ) : null;

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={fil} titre={o ? o.libelle : TITRES[section]} actions={actions} />
        {detail.erreur && (detail.erreur.statut === 403 || detail.erreur.statut === 404
          ? <section className="rh-carte"><AccesRefuseDs erreur={detail.erreur} /></section>
          : <ErreurServeur erreur={detail.erreur} action={<Bouton petit onClick={detail.recharger}>Réessayer</Bouton>} />)}
        {!o && !detail.erreur && <Squelette hauteur={160} />}
        {o && (
          <>
            <Identite o={o} mandats={droits.mandats} />
            {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
            {o.etat === 'BROUILLON' && (
              <BandeauInfo ton="alerte" titre="Organisation en préparation">
                Aucun mandat ne peut être confirmé et aucun acte accompli avant sa mise en service par un administrateur autre que celui qui l'a créée.
              </BandeauInfo>
            )}
            {o.etat === 'EN_SOMMEIL' && <BandeauInfo ton="info" titre="Organisation en sommeil">Les actes sont suspendus jusqu'à sa remise en service.</BandeauInfo>}
            {o.etat === 'DISSOUTE' && <BandeauInfo ton="info" titre="Organisation dissoute">Consultation seule : aucun acte n'est plus possible.</BandeauInfo>}
            <NavOrganisation orgId={o.id} actif={section} modules={modulesVisibles(droits)} />
            {section === 'instances' && (
              <InstancesSection organisation={o} administreStructure={administreStructure} administreMandats={administreMandats}
                onStructureModifiee={detail.recharger} />
            )}
            {section === 'instances' && <CarteDeports organisation={o} droits={droits} />}
            {section === 'dispositifs' && <DispositifsSection organisation={o} peutPreparer={droits.a('ds_dispositif_preparer')} />}
            {section === 'a-traiter' && <ATraiterSection organisation={o} />}
            {section === 'historique' && <HistoriqueSection organisation={o} />}
            {section === 'pieces' && <PiecesSection organisation={o} />}
            {section === 'prets' && <PretsSection organisation={o} droits={droits} />}
            {section === 'aides' && <AidesSection organisation={o} droits={droits} />}
            {section === 'campagnes' && <CampagnesSection organisation={o} droits={droits} />}
            {section === 'tresorerie' && <TresorerieSection organisation={o} droits={droits} />}
            {section === 'comptabilite' && <ComptabiliteSection organisation={o} droits={droits} />}
            {section === 'retenues' && <RetenuesSection organisation={o} droits={droits} />}
            {section === 'rapports' && <RapportsSection organisation={o} droits={droits} />}
          </>
        )}

        {fenetre === 'valider' && o && (
          <FenetreConfirmer titre="Mettre en service" Icone={CheckCircle2} libelleBouton="Mettre en service"
            texte="La mise en service revient à un administrateur autre que celui qui a créé l'organisation. Les organes encore à valider qu'il n'a pas créés sont validés avec elle."
            onFermer={() => setFenetre(null)} onConfirmer={async () => { await ds.validerOrganisation(o.id); apres('Organisation mise en service.'); }} />
        )}
        {fenetre === 'etat' && o && (
          <FenetreEtatOrganisation organisation={o} onFermer={() => setFenetre(null)} onFait={() => apres('État de l\'organisation modifié.')} />
        )}
      </div>
    </Layout>
  );
}
